import React, { useEffect, useRef, useState } from 'react';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import WestOutlinedIcon from '@mui/icons-material/KeyboardArrowLeft';
import { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { LinearProgress, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { io } from 'socket.io-client';
import cx from 'classnames';

import { FOLDER_CONTENT_FILTER, TRANSCODING_EVENTS, TRANSCODING_STATUS, VIDEOFEED_TYPE, VIDEOPAGE_TYPE } from '../../../enums/ENUMS';
import { refetchFeeds } from '../../../store/videoFeedsList/videoFeedsListSlice';
import VideoMyComposerList from '../../../components/modules/VideoComposerList';
import SidebarSharedIcon from '../../../assets/icons/sidebarSharedIcon.svg';
import ComposerDownloadRoot from '../../../components/Composer/ComposerDownload/ComposerDownloadRoot';
import AdditionalAnglesUploadModal from '../../../components/VideofeedsList/AdditionalAnglesUploadModal';

import SidebarComposerIcon from '../../../assets/icons/whiteComposer.svg';
import MinimizedUploadModal from './SiderbarModals/MinimizedUploadModal';
import VideoFileList from '../../../components/modules/VideoFileList';
import { ContainedButton } from '../../../components/modules/Buttons';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getFoldersThunk } from '../../../store/folders/foldersSlice';
import SidebarFolderIcon from '../../../assets/icons/folder-open.svg';
import UploadContentModal from './SiderbarModals/UploadContentModal';
import UploadHoursIcon from '../../../assets/icons/uploadHours.svg';
import DownloadHoursIcon from '../../../assets/icons/pure-download.svg';
import MinimizeIcon from '../../../assets/icons/minimizeIcon.svg';
import UploadIcon from '../../../assets/icons/uploadFolder.svg';
import { StorageIcon } from '../../../assets/icons/storageIcon';
import AddFolderModal from './SiderbarModals/AddFolderModal';
import AddIcon from '../../../assets/icons/addFolder.svg';
import { ROLES, RolesLowerCase } from '../../../enums/ROLES';
import {
  UploadContentState,
  UploadingStatus,
  switchSidebar,
  updateAdditionalAngleUploadFeed,
  updateNotificationTrigger,
  updateShowSidebar,
  updateSocketState,
  updateTranscodingStatus,
  updateUploadingContentState,
} from '../../../store/sharedStore/sharedStoreSlice';
import { setFetchComments } from '../../../store/composers/composersSlice';
import { SocketMessageDto } from '../../../enums';
import { PARENT_ORG_SIDEBAR_MENU, SIDEBAR_MENU } from './SIDEBAR_MENU';
import HoursIndicator from './HoursIndicator';
import { PATHS } from '../../../enums/PATHS';
import styles from './Sidebar.module.scss';
import {
  bytesToGB,
  bootstrapComposerWebPreferences,
  findSearchValue,
  getSidebarVisibilityFromLocalStorage,
  parseQueryString,
  setSidebarVisibiltyToLocalStorage,
  useDimensions,
} from '../../../helpers';
import HoursLimitGraph from './HoursLimitGraph';
import {
  bypassCacheForNextVideofeedsListFetch,
  getOrganizationStorageUsage,
  getUserOrganization,
  haveCollaborativeFolders,
  postRefreshVideofeedStatus,
} from '../../../api';
import { updateComposerPlayControls } from '../../../store/composers/composersSlice';
import { updateVideoControls } from '../../../store/videos/videoSlice';
import { saveMyOrganization, updateMyOrganizationUsage } from '../../../store/user/userSlice';
import { shortcutsInfo } from '../../../consts';
import { FE_VERSION } from '../../../consts/version';
import { enqueueSnackbar } from 'notistack';
import AddCollaborateFolderModal from './SiderbarModals/AddCollaborateModal';
import GlobalSearch from '../GlobalSearch';

type TSidebarProps = {
  currentPath:
    | {
        path: string;
        label: string;
        breadcrumbs: boolean;
        exact: boolean;
        sidebar: boolean;
      }
    | undefined;
};

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} arrow classes={{ popper: className }} />)(
  ({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      display: 'none',
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: 'transparent',
    },
  }),
);

const isCloseToPeriodEnd = (period_end: string) => {
  const targetDate = new Date(period_end);
  const currentDate = new Date();
  const msInDay = 1000 * 60 * 60 * 24;
  const diffDays = Math.abs(targetDate.getTime() - currentDate.getTime()) / msInDay;

  const isWithin30Days = diffDays <= 30;
  if (isWithin30Days) {
    return diffDays.toFixed(0);
  } else {
    false;
  }
};

const ORGANIZATION_USAGE_POLL_INTERVAL_MS = 10_000;

const Sidebar: React.FC<TSidebarProps> = ({ currentPath }) => {
  const [playerSearchExpanded, setPlayerSearchExpanded] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showMinimizedModal, setShowMinimizedModal] = useState(false);
  const [allowNewUpload, setAllowNewUpload] = useState(false);
  const [expirationTimeDifference, setExpirationTimeDifference] = useState(0);
  const [outOfDate, setOutOfDate] = useState(false);
  const [parentPage, setParentPage] = useState(window.localStorage.getItem('parentPage') || '');
  const [storageRatio, setStorageRatio] = useState(0);
  const [orgStorageUsed, setOrgStoragedUsed] = useState<number | null>(null);
  const [hoursLimitRatio, setHoursLimitRatio] = useState(0);
  const [downloadHoursLimitRatio, setDownloadHoursLimitRatio] = useState(0);
  const { widthD, heightD } = useDimensions();

  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const { currentUser, role, myOrganization } = useAppSelector((state) => state.user);
  const { additionalAngleUploadFeed, transcodingStatus, uploadingContentState, socket, showSidebar, isSidebarHidden } = useAppSelector(
    (state) => state.sharedStore,
  );
  const { composerPlayControl } = useAppSelector((state) => state.composersStore);
  const { videoControls, videoFeed } = useAppSelector((state) => state.videoStore);
  const activeFeedId = searchParams.get('feedId');
  const activeVideoFeed =
    videoFeed.find((feed) => feed.reference_timelines?.some((reference) => String(reference.id) === activeFeedId)) ||
    videoFeed.find((feed) => String(feed.id) === activeFeedId);
  const activeVideoFeedTitle = activeVideoFeed?.collection_name || activeVideoFeed?.title;
  const uploadingContentStateRef = useRef(uploadingContentState);
  const reconciledAdditionalAnglesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    uploadingContentStateRef.current = uploadingContentState;
  }, [uploadingContentState]);

  const dispatch = useAppDispatch();
  const { page, limit, sortOption } = useAppSelector((state) => state.folderStore);

  const switchSidebarView = () => {
    setSidebarVisibiltyToLocalStorage(`${!showSidebar}`);
    dispatch(updateShowSidebar(!showSidebar));
  };

  const openAddFolderModal = () => {
    setShowFolderModal(true);
  };

  const folderIdFromQuery = searchParams.get('folderId');
  const folderTrailFromQuery = searchParams.get('folderTrail');
  const isNestedFolderPage = [PATHS.VIDEOFEEDS_LIST.path, PATHS.COLLABORATIVE_VIDEOFEEDS_LIST.path].includes(location.pathname);
  const canAddSubfolder =
    isNestedFolderPage &&
    !!folderIdFromQuery &&
    location.pathname !== PATHS.COLLABORATIVE_VIDEOFEEDS_LIST.path;

  const [showCollaborateModal, setShowCollaborateModal] = useState(false);

  const switchCollaborateModal = () => {
    setShowCollaborateModal(!showCollaborateModal);
  };

  const openUploadModal = () => {
    if (disabledUploads) {
      enqueueSnackbar('Storage hours limit reached or current hours limit period ended', {
        variant: 'warning',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      return;
    }
    if (transcodingStatus === TRANSCODING_STATUS.DEFAULT || allowNewUpload) {
      setShowMinimizedModal(false);
      setShowUploadModal(true);
    } else {
      setShowMinimizedModal(false);
    }
    if (transcodingStatus === TRANSCODING_STATUS.IN_PROGRESS && allowNewUpload) {
      dispatch(updateTranscodingStatus(TRANSCODING_STATUS.DEFAULT));
    }
    if (transcodingStatus === TRANSCODING_STATUS.FINISHED) {
      dispatch(updateTranscodingStatus(TRANSCODING_STATUS.DEFAULT));
      setShowUploadModal(true);
    }
  };

  useEffect(() => {
    if (
      [
        PATHS.MY_FOLDERS.path,
        PATHS.MY_REFLEX_COMPOSERS.path,
        PATHS.SHARED_WITH_ME.path,
        PATHS.TIMELINE.path,
        PATHS.COMPOSER_PAGE.path,
        PATHS.COLLABORATIVE_FOLDERS.path,
      ].includes(history.location.pathname)
    ) {
      window.localStorage.setItem('parentPage', history.location.pathname);
      setParentPage(history.location.pathname);
      dispatch(switchSidebar(false));
    } else {
      dispatch(switchSidebar(true));
    }
  }, [history.location.pathname]);

  const goBack = () => {
    if (composerPlayControl.composerControls.playing) {
      dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playing: false }));
    }
    if (videoControls.playing) {
      dispatch(updateVideoControls({ ...videoControls, playing: false }));
    }

    const page = findSearchValue('page');
    const limit = findSearchValue('limit');
    const sort = findSearchValue('sort');
    const search = findSearchValue('search');
    const parentPath = findSearchValue('parentPage');
    const allContent = findSearchValue('allContent');
    const folderTrail = findSearchValue('folderTrail');
    const contentFilter = findSearchValue('contentFilter');
    const paramsObject = parseQueryString(location.search);
    const folderId = findSearchValue('folderId');
    let wasItSharedComposer = false;
    if (paramsObject?.sharedComposer) {
      wasItSharedComposer = true;
    }

    if (allContent && folderId) {
      history.push(
        `${PATHS.ALL_CONTENT_VIDEOFEEDS.path}?folderId=${paramsObject.folderId}${
          folderTrail ? `&folderTrail=${folderTrail}` : ''
        }${
          contentFilter && contentFilter !== FOLDER_CONTENT_FILTER.ALL
            ? `&contentFilter=${contentFilter}`
            : ''
        }`,
      );
      return;
    } else if (allContent) {
      history.push(PATHS.ALL_CONTENT.path);
      return;
    }

    const parsedFolderTrail = folderTrail
      ? decodeURIComponent(folderTrail)
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean)
      : [];

    if (
      parsedFolderTrail.length > 0 &&
      [PATHS.VIDEOFEEDS_LIST.path, PATHS.SHARED_VIDEOFEEDS_LIST.path, PATHS.COLLABORATIVE_VIDEOFEEDS_LIST.path].includes(location.pathname)
    ) {
      const previousFolderId = parsedFolderTrail[parsedFolderTrail.length - 1];
      const nextTrail = parsedFolderTrail.slice(0, -1).join(',');
      const basePath = location.pathname;
      const nextSearch = new URLSearchParams();
      nextSearch.set('folderId', previousFolderId);

      if (parentPath) {
        nextSearch.set('parentPage', parentPath);
      }
      if (page) {
        nextSearch.set('page', page);
      }
      if (limit) {
        nextSearch.set('limit', limit);
      }
      if (sort) {
        nextSearch.set('sort', sort);
      }
      if (search) {
        nextSearch.set('search', search);
      }
      if (nextTrail) {
        nextSearch.set('folderTrail', nextTrail);
      }
      if (contentFilter && contentFilter !== FOLDER_CONTENT_FILTER.ALL) {
        nextSearch.set('contentFilter', contentFilter);
      }

      history.push(`${basePath}?${nextSearch.toString()}`);
      return;
    }

    if (paramsObject?.pageType?.includes(VIDEOFEED_TYPE.MY_COMPOSER)) {
      if (wasItSharedComposer) {
        history.push(`${PATHS.SHARED_WITH_ME.path}?prevPageWasSharedComposer=true`);
      } else {
        history.push(`${PATHS.MY_REFLEX_COMPOSERS.path}`);
      }
      return;
    } else if (parentPath?.includes('Shared')) {
      history.push(
        `${PATHS.SHARED_VIDEOFEEDS_LIST.path}?folderId=${paramsObject.folderId}&parentPage=${PATHS.SHARED_WITH_ME.label}&page=${page}&limit=${limit}&sort=${sort}&search=${search}${
          folderTrail ? `&folderTrail=${folderTrail}` : ''
        }${
          contentFilter && contentFilter !== FOLDER_CONTENT_FILTER.ALL
            ? `&contentFilter=${contentFilter}`
            : ''
        }`,
      );
      return;
    } else if (parentPath?.includes('My') || !parentPath) {
      history.push(
        `${PATHS.VIDEOFEEDS_LIST.path}?folderId=${paramsObject.folderId}&parentPage=${PATHS.MY_FOLDERS.label}${
          folderTrail ? `&folderTrail=${folderTrail}` : ''
        }${
          contentFilter && contentFilter !== FOLDER_CONTENT_FILTER.ALL
            ? `&contentFilter=${contentFilter}`
            : ''
        }`,
      );
      return;
    }

    if (decodeURIComponent(parentPath) === PATHS.COLLABORATIVE_FOLDERS.label) {
      history.push(
        `${PATHS.COLLABORATIVE_VIDEOFEEDS_LIST.path}?folderId=${paramsObject.folderId}&parentPage=${PATHS.COLLABORATIVE_FOLDERS.label}${
          folderTrail ? `&folderTrail=${folderTrail}` : ''
        }${
          contentFilter && contentFilter !== FOLDER_CONTENT_FILTER.ALL
            ? `&contentFilter=${contentFilter}`
            : ''
        }`,
      );
      return;
    }
    history.replace(parentPage);
  };

  const maximizeModal = () => {
    setShowMinimizedModal(false);
    if (transcodingStatus === TRANSCODING_STATUS.DEFAULT) {
      setShowUploadModal(true);
    }
  };

  useEffect(() => {
    if (currentUser) {
      dispatch(
        updateSocketState(
          io(`${process.env.REACT_APP_SOCKET_URL}`, {
            query: { userId: currentUser?.id },
          }),
        ),
      );
    }

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [currentUser]);

  const parseTranscodingEvent = (notificationMessage: SocketMessageDto) => {
    const uploadingContents = [...uploadingContentStateRef.current];

    const additionalAngleIndex = uploadingContents.findIndex(
      (item) =>
        item.uploadMode === 'additional-angle' &&
        item.videoId === notificationMessage.data,
    );
    const indexOfContent =
      additionalAngleIndex !== -1
        ? additionalAngleIndex
        : uploadingContents.findIndex(
            (item) => item.videofeedId === notificationMessage.videofeedId,
          );
    const pkg = uploadingContents[indexOfContent];
    setTimeout(() => {
      dispatch(refetchFeeds());
    }, 5000);
    // @ts-ignore
    if (pkg?.status === 'finished' || pkg?.status === 'completed' || pkg?.status === 'failed') {
      return;
    }

    const newItem: UploadContentState = {
      ...pkg,
      videofeedId: pkg?.videofeedId || notificationMessage.videofeedId!,
      videofeedTitle: notificationMessage.videofeedTitle!,
      status: UploadingStatus.TRANSCODING,
    };

    let transcodingStatus: TRANSCODING_STATUS = TRANSCODING_STATUS.IN_PROGRESS;
    switch (notificationMessage.event) {
      case TRANSCODING_EVENTS.STARTED:
        if (!allowNewUpload) {
          setAllowNewUpload(true);
        }
        break;
      case TRANSCODING_EVENTS.COMPLETED:
        transcodingStatus = TRANSCODING_STATUS.FINISHED;
        newItem.status = UploadingStatus.FINISHED;

        setAllowNewUpload(false);
        dispatch(refetchFeeds());
        setTimeout(() => {
          dispatch(getFoldersThunk({ page, limit, sortOption }));
        }, 5000);
        break;
      case TRANSCODING_EVENTS.FAILED:
        transcodingStatus = TRANSCODING_STATUS.DEFAULT;
        newItem.status = UploadingStatus.FAILED;

        setAllowNewUpload(false);
        dispatch(getFoldersThunk({ page, limit, sortOption }));
        dispatch(refetchFeeds());
        break;

      default:
        break;
    }

    if (indexOfContent !== -1) {
      uploadingContents.splice(indexOfContent, 1, newItem);
      // Last Part Transcoding Event
      dispatch(updateUploadingContentState(uploadingContents));
    }
    dispatch(updateTranscodingStatus(transcodingStatus));

    if ([TRANSCODING_EVENTS.COMPLETED, TRANSCODING_EVENTS.FAILED].includes(notificationMessage.event as TRANSCODING_EVENTS)) {
      setVideoFeedIdAndStatus((prev) =>
        prev.filter(
          (item) =>
            !(
              item.videofeedId === notificationMessage.videofeedId &&
              item.type === notificationMessage.type &&
              String(item.data || '') === String(notificationMessage.data || '')
            ),
        ),
      );
    }
  };

  const [videoFeedIdAndStatus, setVideoFeedIdAndStatus] = useState<{ type: string; videofeedId: string; event: string; data?: string }[]>([]);

  const updateHoursRatios = (data: typeof myOrganization) => {
    if (data?.hours_limit) {
      const hoursRatio = data.hours_limit > 0 ? (data.hours_limit_used / data.hours_limit) * 100 : 0;
      setHoursLimitRatio(hoursRatio > 100 ? 100 : hoursRatio);
    } else {
      setHoursLimitRatio(0);
    }

    if (data?.download_hours_limit) {
      const downloadHoursRatio = data.download_hours_limit > 0 ? (data.download_hours_limit_used / data.download_hours_limit) * 100 : 0;
      setDownloadHoursLimitRatio(downloadHoursRatio > 100 ? 100 : downloadHoursRatio);
    } else {
      setDownloadHoursLimitRatio(100);
    }
  };

  const fetchOrganization = async (forceRefresh = false) => {
    try {
      const { data } = await getUserOrganization(forceRefresh);
      dispatch(saveMyOrganization(data));
      updateHoursRatios(data);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchOrganization();
  }, []);

  useEffect(() => {
    if (!currentUser?.id) return;

    const pendingAdditionalAngles = uploadingContentState.filter(
      (item) =>
        item.uploadMode === 'additional-angle' &&
        item.status === UploadingStatus.TRANSCODING &&
        item.videofeedId,
    );
    const itemsToRefresh = pendingAdditionalAngles.filter((item) => {
      const key = String(item.videoId || item.id || item.videofeedId);
      if (reconciledAdditionalAnglesRef.current.has(key)) return false;
      reconciledAdditionalAnglesRef.current.add(key);
      return true;
    });

    if (!itemsToRefresh.length) return;

    let cancelled = false;
    const refreshPendingAngles = async () => {
      const feedIds = Array.from(
        new Set(itemsToRefresh.map((item) => String(item.videofeedId))),
      );
      await Promise.allSettled(
        feedIds.map((feedId) => postRefreshVideofeedStatus(feedId)),
      );
      if (cancelled) return;

      bypassCacheForNextVideofeedsListFetch();
      dispatch(refetchFeeds());
      dispatch(getFoldersThunk({ page, limit, sortOption }));
    };

    void refreshPendingAngles();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, dispatch, limit, page, sortOption, uploadingContentState]);

  let lastNotify = 0;
  // Keep track of snackbars currently shown to avoid duplicates
  const activeSnackKeysRef = useRef<Set<string>>(new Set());
  const getSnackKey = (m: SocketMessageDto) =>
    `${m.type || ''}:${(m.event || '').toLowerCase()}:${m.videofeedId || ''}:${m.data || ''}`;
  useEffect(() => {
    if (currentUser && socket) {
      socket.on('transcoding', (message: string) => {
        const notificationMessage: SocketMessageDto = JSON.parse(message);
        console.log(notificationMessage);
        fetchStorageUsage();
        fetchOrganization(true);

        if (notificationMessage.videofeedId) {
          if (
            videoFeedIdAndStatus?.find(
              (v) =>
                v.videofeedId === notificationMessage.videofeedId &&
                notificationMessage.type === v.type &&
                String(notificationMessage.event || '').toLowerCase() === String(v.event || '').toLowerCase() &&
                String(notificationMessage.data || '') === String(v.data || ''),
            )
          ) {
            return;
          }
          const arr = [
            ...videoFeedIdAndStatus,
            {
              type: notificationMessage.type,
              videofeedId: notificationMessage.videofeedId,
              event: notificationMessage.event,
              data: notificationMessage.data,
            },
          ];
          setVideoFeedIdAndStatus(arr);
          parseTranscodingEvent(notificationMessage);
        }

        const now = new Date();
        const formattedTime =
          `${now.getDate()} ${now.toLocaleString('en', { month: 'short' })} ` +
          `${String(now.getHours()).padStart(2, '0')}:` +
          `${String(now.getMinutes()).padStart(2, '0')}:` +
          `${String(now.getSeconds()).padStart(2, '0')}`;

        // if (notificationMessage?.event?.toLocaleLowerCase() === 'failed') {
        //   enqueueSnackbar(`${notificationMessage.message}. (${formattedTime})`, {
        //     preventDuplicate: true,
        //     variant: 'error',
        //     anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
        //     persist: true,
        //   });
        // } else if (notificationMessage?.event?.toLocaleLowerCase() === 'completed') {
        //   enqueueSnackbar(`${notificationMessage.message}. (${formattedTime})`, {
        //     preventDuplicate: true,
        //     variant: 'success',
        //     anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
        //     persist: true,
        //   });
        // } else {
        //   enqueueSnackbar(`${notificationMessage.message}. (${formattedTime})`, {
        //     preventDuplicate: true,
        //     anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
        //     autoHideDuration: 8000,
        //   });
        // }

        const key = getSnackKey(notificationMessage);
        if (activeSnackKeysRef.current.has(key)) return; // already shown
        activeSnackKeysRef.current.add(key);

        const baseOpts = {
          key,
          preventDuplicate: true,
          anchorOrigin: { horizontal: 'left', vertical: 'bottom' } as const,
          onExited: () => activeSnackKeysRef.current.delete(key),
        };

        const text = `${notificationMessage.message}. (${formattedTime})`;
        const ev = (notificationMessage?.event || '').toLowerCase();
        if (ev === 'failed') {
          enqueueSnackbar(text, { ...baseOpts, variant: 'error', persist: true });
        } else if (ev === 'completed') {
          enqueueSnackbar(text, { ...baseOpts, variant: 'success', persist: true });
        } else {
          enqueueSnackbar(text, { ...baseOpts, autoHideDuration: 8000 });
        }

        if (notificationMessage?.type === 'transcoding' && notificationMessage.event === 'started') {
          const searchString = String(notificationMessage.videofeedId || '');
          for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (!key || !key.startsWith('=videofeedToResume=')) continue;

            const raw = localStorage.getItem(key);
            if (!raw) continue;

            try {
              const pkg = JSON.parse(raw);
              if (String(pkg?.id || '') === searchString) {
                localStorage.removeItem(key);
              }
            } catch {
              // ignore malformed resume payloads
            }
          }
        } else if (notificationMessage?.type === 'transcoding') {
          setTimeout(() => {
            fetchOrganization(true);
          }, 5000);
        }
      });

      setTimeout(() => {
        dispatch(refetchFeeds());
      }, 8000);

      socket.on('storage', (msg: string) => {
        /*
          msg: {
            type: 'storage',
            event: 'changed',
            message: Storage usage changed,
            data: '{ storage_usage: organization.storage_usage }',
          }
          as string
        */
        const msgObj = JSON.parse(msg);
        if (myOrganization && msgObj.type === 'storage' && msgObj.event === 'changed') {
          fetchStorageUsage();
          fetchOrganization(true);
        }
      });

      socket.on('notification', (e) => {
        let notificationMessage: SocketMessageDto | null = null;
        try {
          notificationMessage = JSON.parse(e);
        } catch {
          notificationMessage = null;
        }

        const currentFeedId = findSearchValue('feedId');
        if (
          notificationMessage?.videofeedId &&
          notificationMessage?.userEmail &&
          currentFeedId &&
          String(notificationMessage.videofeedId) === String(currentFeedId) &&
          String(notificationMessage.userEmail) !== String(currentUser?.email || '')
        ) {
          dispatch(setFetchComments());
        }

        const now = Date.now();
        if (now - lastNotify < 1000) return;
        lastNotify = now;
        dispatch(updateNotificationTrigger(now));
      });

      const onUserSettingsChanged = () => {
        void bootstrapComposerWebPreferences();
      };
      const onOrganizationUsageChanged = (message: string) => {
        try {
          const event = JSON.parse(message);
          const usage = typeof event?.data === 'string' ? JSON.parse(event.data) : event?.data;
          if (String(usage?.organizationId) !== String(myOrganization?.id)) return;
          setOrgStoragedUsed(Number(usage.storage_usage) || 0);
          setStorageRatio(
            Number(usage.storage_limit) > 0
              ? (Number(usage.storage_usage) / Number(usage.storage_limit)) * 100
              : 0,
          );
          dispatch(updateMyOrganizationUsage(usage));
        } catch (error) {
          console.error('Failed to update organisation usage from socket', error);
        }
      };
      socket.on('user_settings', onUserSettingsChanged);
      socket.on('organization_usage', onOrganizationUsageChanged);

      return () => {
        socket.off('user_settings', onUserSettingsChanged);
        socket.off('organization_usage', onOrganizationUsageChanged);
      };
    }
  }, [currentUser?.id, myOrganization?.id, socket?.id]);

  useEffect(() => {
    if (myOrganization?.id) {
      fetchStorageUsage();
      updateHoursRatios(myOrganization);

      if (myOrganization.period_end) {
        const targetDate = new Date(myOrganization.period_end);
        targetDate.setDate(targetDate.getDate() + 1);
        const currentDate = new Date();
        const msInDay = 1000 * 60 * 60 * 24;
        const diffDays = Math.abs(targetDate.getTime() - currentDate.getTime()) / msInDay;
        const diffHours = diffDays * 24;

        const isWithin30Days = diffDays <= 30;

        if (isWithin30Days) {
          if (targetDate.getTime() < currentDate.getTime()) {
            setOutOfDate(true);
          } else {
            setExpirationTimeDifference(+diffHours.toFixed(0));
          }
        }
      }
    }
  }, [myOrganization]);

  useEffect(() => {
    const sidebarVisibility = getSidebarVisibilityFromLocalStorage();
    if (sidebarVisibility?.length) {
      dispatch(updateShowSidebar(sidebarVisibility === 'false' ? false : true));
    }
    if (widthD <= 1024) {
      dispatch(updateShowSidebar(false));
    }
  }, [widthD]);

  const fetchStorageUsage = async (force = false) => {
    if (
      !force &&
      orgStorageUsed !== null &&
      Number(orgStorageUsed.toFixed(2)) ===
        Number(Number(myOrganization.storage_usage || 0).toFixed(2))
    ) return;
    if (!myOrganization.id) return;

    try {
      const res = await getOrganizationStorageUsage(myOrganization.id);
      const storageUsage = bytesToGB(res.data, 2);
      setStorageRatio((storageUsage / myOrganization.storage_limit) * 100);
      setOrgStoragedUsed(storageUsage);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const usagePollingActive = showUploadModal || Boolean(additionalAngleUploadFeed);
    if (!usagePollingActive || !currentUser?.id || !myOrganization?.id) return;

    const refreshUsage = async () => {
      await fetchStorageUsage(true);
      await fetchOrganization(true);
    };

    void refreshUsage();
    const intervalId = window.setInterval(() => void refreshUsage(), ORGANIZATION_USAGE_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [additionalAngleUploadFeed, currentUser?.id, myOrganization?.id, showUploadModal]);

  const [disabledUploads, setDisabledUploads] = useState(false);

  const [showParentFoldersTab, setShowParentFoldersTab] = useState(false);
  let alreadyFetched = false;
  const organizationHaveCollFolders = async () => {
    if (alreadyFetched) return;
    try {
      const res = await haveCollaborativeFolders();
      setShowParentFoldersTab(res);
      alreadyFetched = true;
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (myOrganization?.id) {
      let disabled = false;
      if (myOrganization?.type === 'parent') {
        setShowParentFoldersTab(true);
      } else if (myOrganization?.type === 'child') {
        organizationHaveCollFolders();
      }
      if (+myOrganization?.hours_limit_used >= +myOrganization?.hours_limit) {
        disabled = true;
      }

      if (+myOrganization?.storage_usage >= +myOrganization?.storage_limit) {
        disabled = true;
      }

      if (myOrganization?.period_end) {
        const givenDate = new Date(myOrganization.period_end);
        givenDate.setDate(givenDate.getDate() + 1);

        const today = new Date();
        if (givenDate.getTime() < today.getTime()) {
          disabled = true;
        }
      }

      if (!myOrganization?.period_end || !myOrganization?.period_start) {
        disabled = true;
      }
      setDisabledUploads(disabled);
    }
  }, [myOrganization]);

  const totalMinutes = Math.round(myOrganization.download_hours_limit_used * 60); // Convert hours to total minutes
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const sidebarVisible = !isSidebarHidden && ![PATHS.ALL_CONTENT.path, PATHS.ALL_CONTENT_VIDEOFEEDS.path].includes(location.pathname);
  const playerSidebar = searchParams.get('fileside') === 'true';
  const compactSidebar = !showSidebar || !currentPath?.sidebar;

  return (
    <>
      <div
        data-drawing-sidebar
        className={compactSidebar ? styles.minimizedSidebar : styles.sidebar}
        style={{
          minHeight: window.location.search.includes('fileside=true') ? 0 : 'calc(100vh - 90px)',
          display: sidebarVisible ? 'flex' : 'none',
          flexDirection: 'column',
        }}
      >
        {sidebarVisible && !playerSidebar && role && role !== ROLES.SUPER_ADMIN && (
          <div className={styles.globalSearch}>
            <GlobalSearch compact={!showSidebar || !currentPath?.sidebar} allContent={searchParams.get('allContent') === 'true'} />
          </div>
        )}
        {playerSidebar ? (
          <>
            <div className={cx(styles.playerHeader, compactSidebar && styles.playerHeaderCompact)}>
              <div className={styles.playerActions}>
                <ContainedButton onClick={goBack} className={cx(styles.playerBackButton, playerSearchExpanded && styles.playerBackButtonCollapsed)} ariaLabel="Go Back">
                  <WestOutlinedIcon sx={{ width: 16, height: 16 }} />
                  {!compactSidebar && <span>Go Back</span>}
                </ContainedButton>
                {sidebarVisible && role && role !== ROLES.SUPER_ADMIN && (
                  <GlobalSearch compact={compactSidebar} expandOnHover className={styles.playerSearch}
                    onExpandedChange={setPlayerSearchExpanded} allContent={searchParams.get('allContent') === 'true'} />
                )}
              </div>
              {showSidebar && activeVideoFeedTitle && (
                <Tooltip title={activeVideoFeedTitle} arrow>
                  <div className={styles.compactVideoFeedTitle}>{activeVideoFeedTitle}</div>
                </Tooltip>
              )}
            </div>
            <div className={showSidebar ? styles.fileList : styles.miniFileList}>
              {window.location.search.includes(`pageType=${VIDEOPAGE_TYPE.MY_COMPOSER}`) ? <VideoMyComposerList /> : <VideoFileList />}
            </div>
          </>
        ) : (
          <>
            <div className={styles.menu}>
              {(myOrganization.type === 'parent' ? PARENT_ORG_SIDEBAR_MENU : SIDEBAR_MENU).map((menu) => {
                if (menu.path === PATHS.COLLABORATIVE_FOLDERS.path && !showParentFoldersTab) {
                  return null;
                }

                return (
                  role &&
                  menu.roles.includes(role) && (
                    <NavLink
                      to={menu.path}
                      key={menu.label}
                      exact={menu.exact}
                      className={cx(decodeURIComponent(window.location.pathname).includes(menu.path) && styles.activeItem, styles.item)}
                    >
                      {menu.path === PATHS.COLLABORATIVE_FOLDERS.path && (
                        <img src={SidebarFolderIcon} style={{ height: '20px' }} alt={`${menu.label}`} />
                      )}
                      {/* {menu.path === PATHS.ALL_CONTENT.path && (
                        <img src={AllContentIcon} style={{ height: '20px' }} alt={`${menu.label}`} />
                      )} */}
                      {menu.path === PATHS.SHARED_WITH_ME.path && (
                        <img src={SidebarSharedIcon} style={{ height: '20px' }} alt={`${menu.label}`} />
                      )}
                      {menu.path === PATHS.MY_REFLEX_COMPOSERS.path && <img src={SidebarComposerIcon} alt={`${menu.label}`} />}
                      {menu.path === PATHS.MY_FOLDERS.path && (
                        <img src={SidebarFolderIcon} style={{ height: '20px' }} alt={`${menu.label}`} />
                      )}
                      {showSidebar && <span>{menu.label}</span>}
                    </NavLink>
                  )
                );
              })}
            </div>
          </>
        )}

        <AddFolderModal
          open={showFolderModal}
          setOpen={setShowFolderModal}
          parentFolderId={isNestedFolderPage ? folderIdFromQuery : undefined}
        />

        {currentUser?.id && (
          <AddCollaborateFolderModal currentUserId={currentUser?.id} open={showCollaborateModal} setOpen={switchCollaborateModal} />
        )}

        <UploadContentModal
          allowNewUpload={allowNewUpload}
          open={showUploadModal}
          setOpen={setShowUploadModal}
          setMinimize={setShowMinimizedModal}
          showMinimizedModal={showMinimizedModal}
          disabled={disabledUploads}
        />

        <MinimizedUploadModal open={showMinimizedModal} setOpen={setShowMinimizedModal} setMaximize={maximizeModal} />

        {currentUser?.role.title !== RolesLowerCase.VIEWER && (
          <div className={styles.actionBtns}>
            {location.pathname === PATHS.MY_FOLDERS.path && (
              <div onClick={openAddFolderModal}>
                <img src={AddIcon} alt="add folder" />
                {showSidebar && <div>Add Folder</div>}
              </div>
            )}

            {location.pathname === PATHS.COLLABORATIVE_FOLDERS.path && myOrganization?.type === 'parent' && (
              <div onClick={switchCollaborateModal}>
                <img src={AddIcon} alt="add folder" />
                {showSidebar && <div>Add Collaborative Folder</div>}
              </div>
            )}

            {canAddSubfolder && (
              <div onClick={openAddFolderModal}>
                <img src={AddIcon} alt="add subfolder" />
                {showSidebar && <div>Add Subfolder</div>}
              </div>
            )}

            {location.pathname === PATHS.VIDEOFEEDS_LIST.path && (
              <div onClick={openUploadModal} className={cx(disabledUploads ? styles.disabledUploads : '')}>
                <img src={UploadIcon} alt="upload content" />
                {showSidebar && <div>Upload Content</div>}
              </div>
            )}

            {location.pathname === PATHS.COLLABORATIVE_VIDEOFEEDS_LIST.path && (
              <div onClick={openUploadModal} className={cx(disabledUploads ? styles.disabledUploads : '')}>
                <img src={UploadIcon} alt="upload content" />
                {showSidebar && <div>Upload Content</div>}
              </div>
            )}
          </div>
        )}
        {!window.location.search.includes('fileside=true') &&
          currentUser &&
          [RolesLowerCase.ANALYST, RolesLowerCase.ORGANIZATION_ADMIN].includes(currentUser.role.title as RolesLowerCase) && (
            <div className={styles.storageInfo}>
              {showSidebar ? (
                <div>
                  <div className={styles.usageCard}>
                    <StorageIcon
                      className={cx(
                        styles.storageIcon,
                        showSidebar && +storageRatio.toFixed(1) > 50 && +storageRatio.toFixed(1) < 95 ? styles.yellowText : '',
                        showSidebar && +storageRatio.toFixed(1) >= 95 ? styles.redText : '',
                      )}
                      color={
                        +storageRatio.toFixed(1) > 50 && +storageRatio.toFixed(1) < 95
                          ? '#ffb319'
                          : +storageRatio.toFixed(1) >= 95
                            ? '#d33030'
                            : ''
                      }
                    />
                    <div className={styles.usageTitle}>Storage limit</div>
                    <div
                      className={cx(
                        styles.storageRatio,
                        +storageRatio.toFixed(1) > 50 && +storageRatio.toFixed(1) < 95 ? styles.yellowText : '',
                        +storageRatio.toFixed(1) >= 95 ? styles.redText : '',
                      )}
                    >
                      {storageRatio.toFixed(1)}% used
                    </div>
                    <Tooltip
                      placement={heightD < 768 ? 'right' : 'bottom'}
                      title={`${orgStorageUsed?.toFixed(2)} GB of ${myOrganization.storage_limit} GB used`}
                    >
                      <div className={styles.containerForProgress}>
                        <LinearProgress variant="determinate" value={Math.round(storageRatio)} className={styles.linearProgress} />
                      </div>
                    </Tooltip>
                    <div className={styles.usageMeta}>{orgStorageUsed?.toFixed(2)} GB out of {myOrganization.storage_limit} GB</div>
                    <div>
                      <div className={styles.fullstoragetext}>
                        {+storageRatio.toFixed(1) >= 100 &&
                          `Storage limit reached! Need more?
                                  Contact Fulcrum to adjust your subscription!`}
                      </div>
                    </div>
                  </div>
                  {myOrganization?.id && (
                    <BootstrapTooltip
                      placement="right"
                      arrow
                      title={
                        <HoursLimitGraph
                          limit={myOrganization.hours_limit}
                          used={myOrganization.hours_limit_used}
                          startDate={myOrganization.period_start}
                          endDate={myOrganization.period_end}
                          usageData={myOrganization.monthly_hours_usage}
                          ratio={hoursLimitRatio.toFixed(1)}
                          projected_hours_at_the_end={myOrganization?.projected_hours_at_the_end}
                          deletedHoursCurrentPeriod={myOrganization?.deleted_hours_current_period}
                        />
                      }
                    >
                      <div className={styles.uploadHoursInfo}>
                        <img
                          style={{
                            filter:
                              +hoursLimitRatio.toFixed(1) > 50 && +hoursLimitRatio.toFixed(1) < 95
                                ? 'invert(23%) sepia(100%) saturate(7490%) hue-rotate(-10deg) brightness(96%) contrast(112%)'
                                : +hoursLimitRatio.toFixed(1) >= 95
                                  ? 'invert(30%) sepia(90%) saturate(6000%) hue-rotate(-10deg) brightness(80%) contrast(120%)'
                                  : 'none',
                          }}
                          className={styles.uploadHoursIcon}
                          src={UploadHoursIcon}
                          alt="upload hours"
                        />
                        <div
                          className={cx(
                            styles.hoursText,
                            +hoursLimitRatio.toFixed(1) > 50 && +hoursLimitRatio.toFixed(1) < 95 ? styles.yellowText : '',
                            +hoursLimitRatio.toFixed(1) >= 95 ? styles.redText : '',
                          )}
                        >
                          Upload Hours limit
                        </div>
                        <div
                          className={cx(
                            styles.hoursText,
                            +hoursLimitRatio.toFixed(1) > 50 && +hoursLimitRatio.toFixed(1) < 95 ? styles.yellowText : '',
                            +hoursLimitRatio.toFixed(1) >= 95 ? styles.redText : '',
                          )}
                        >
                          {hoursLimitRatio.toFixed(1)} % used{' '}
                        </div>

                        <div className={styles.hoursProgress}>
                          <HoursIndicator
                            style={{ marginTop: '14px' }}
                            value={+myOrganization?.hours_limit_used}
                            max={myOrganization.hours_limit}
                          />
                        </div>
                        <div className={styles.usageMeta}>{(+myOrganization.hours_limit_used).toFixed(1)} hours out of {myOrganization.hours_limit}</div>
                        <div className={styles.redWarning}>
                          {myOrganization?.period_end && expirationTimeDifference !== 0 && !outOfDate
                            ? expirationTimeDifference > 24
                              ? `Remaining hours expire in ${(expirationTimeDifference / 24).toFixed(0)} day(s)`
                              : `Remaining hours expire in ${expirationTimeDifference} hour(s)`
                            : ''}
                          {myOrganization?.period_end && outOfDate && (
                            <div>
                              {myOrganization?.download_hours_limit
                                ? 'Your Upload and Download Hours have expired.'
                                : 'Your Upload Hours have expired.'}
                              <br /> Please contact Fulcrum for more hours.
                            </div>
                          )}
                        </div>
                      </div>
                    </BootstrapTooltip>
                  )}
                  {myOrganization?.id && !!myOrganization?.download_hours_limit && (
                    <div className={styles.downloadHoursInfo}>
                      <img
                        style={{
                          filter:
                            +downloadHoursLimitRatio.toFixed(1) > 50 && +downloadHoursLimitRatio.toFixed(1) < 95
                              ? 'invert(23%) sepia(100%) saturate(7490%) hue-rotate(-10deg) brightness(96%) contrast(112%)'
                              : +downloadHoursLimitRatio.toFixed(1) >= 95
                                ? 'invert(30%) sepia(90%) saturate(6000%) hue-rotate(-10deg) brightness(80%) contrast(120%)'
                                : 'none',
                          height: 24,
                        }}
                        className={styles.downloadHoursIcon}
                        src={DownloadHoursIcon}
                        alt="download hours"
                      />
                      <div
                        className={cx(
                          styles.hoursText,
                          +downloadHoursLimitRatio.toFixed(1) > 50 && +downloadHoursLimitRatio.toFixed(1) < 95 ? styles.yellowText : '',
                          +downloadHoursLimitRatio.toFixed(1) >= 95 ? styles.redText : '',
                        )}
                      >
                        Download Hours limit
                      </div>
                      <div
                        className={cx(
                          styles.hoursText,
                          +downloadHoursLimitRatio.toFixed(1) > 50 && +downloadHoursLimitRatio.toFixed(1) < 95 ? styles.yellowText : '',
                          +downloadHoursLimitRatio.toFixed(1) >= 95 ? styles.redText : '',
                        )}
                      >
                        {downloadHoursLimitRatio.toFixed(1)} % used
                      </div>
                      <div className={styles.hoursProgress}>
                        <HoursIndicator
                          style={{ marginTop: '14px' }}
                          value={+myOrganization?.download_hours_limit_used}
                          max={myOrganization.download_hours_limit}
                        />
                      </div>
                      <div className={styles.usageMeta}>
                        {(+myOrganization.download_hours_limit_used).toFixed(1)} hours out of {myOrganization.download_hours_limit}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.minumizedStorageRation}>
                  <div className={styles.minimizedUsageCard}>
                    <StorageIcon
                      className={cx(
                        styles.storageIcon,
                        showSidebar && +storageRatio.toFixed(1) > 50 && +storageRatio.toFixed(1) < 95 ? styles.yellowText : '',
                        showSidebar && +storageRatio.toFixed(1) >= 95 ? styles.redText : '',
                      )}
                      color={
                        +storageRatio.toFixed(1) > 50 && +storageRatio.toFixed(1) < 95
                          ? '#ffb319'
                          : +storageRatio.toFixed(1) >= 95
                            ? '#d33030'
                            : ''
                      }
                    />
                    <Tooltip placement="right" title={`${orgStorageUsed?.toFixed(2)} GB of ${myOrganization.storage_limit} GB used`}>
                      <div
                        className={cx(
                          styles.storageRatio,
                          +storageRatio.toFixed(1) > 50 && +storageRatio.toFixed(1) < 95 ? styles.yellowText : '',
                          +storageRatio.toFixed(1) >= 95 ? styles.redText : '',
                        )}
                      >
                        {storageRatio === storageRatio && storageRatio.toFixed() + '%'}
                      </div>
                    </Tooltip>
                    <div
                      className={cx(
                        styles.storageUsedText,
                        +storageRatio.toFixed(1) > 50 && +storageRatio.toFixed(1) < 95 ? styles.yellowText : '',
                        +storageRatio.toFixed(1) >= 95 ? styles.redText : '',
                      )}
                    >
                      {' '}
                      Storage <br /> used{' '}
                    </div>
                  </div>

                  <BootstrapTooltip
                    placement="right"
                    arrow
                    title={
                      <HoursLimitGraph
                        limit={myOrganization.hours_limit}
                        used={myOrganization.hours_limit_used}
                        startDate={myOrganization.period_start}
                        endDate={myOrganization.period_end}
                        usageData={myOrganization.monthly_hours_usage}
                        ratio={hoursLimitRatio.toFixed(1)}
                        projected_hours_at_the_end={myOrganization?.projected_hours_at_the_end}
                        deletedHoursCurrentPeriod={myOrganization?.deleted_hours_current_period}
                      />
                    }
                  >
                    <div className={styles.minimizedUsageCard}>
                      <img
                        className={styles.uploadHoursIcon}
                        style={{
                          marginBottom: '6px',
                          marginTop: '12px',
                          filter:
                            +hoursLimitRatio.toFixed(1) > 50 && +hoursLimitRatio.toFixed(1) < 95
                              ? 'invert(23%) sepia(100%) saturate(7490%) hue-rotate(-10deg) brightness(96%) contrast(112%)'
                              : +hoursLimitRatio.toFixed(1) >= 95
                                ? 'invert(30%) sepia(90%) saturate(6000%) hue-rotate(-10deg) brightness(80%) contrast(120%)'
                                : 'none',
                        }}
                        src={UploadHoursIcon}
                        alt="upload hours"
                      />

                      <div
                        className={cx(
                          styles.storageRatio,
                          +hoursLimitRatio.toFixed(1) > 50 && +hoursLimitRatio.toFixed(1) < 95 ? styles.yellowText : '',
                          +hoursLimitRatio.toFixed(1) >= 95 ? styles.redText : '',
                        )}
                      >
                        {hoursLimitRatio === hoursLimitRatio && hoursLimitRatio.toFixed() + '%'}
                      </div>
                      <div
                        className={cx(
                          styles.storageUsedText,
                          +hoursLimitRatio.toFixed(1) > 50 && +hoursLimitRatio.toFixed(1) < 95 ? styles.yellowText : '',
                          +hoursLimitRatio.toFixed(1) >= 95 ? styles.redText : '',
                        )}
                      >
                        {' '}
                        Upload <br /> Hours
                      </div>
                    </div>
                  </BootstrapTooltip>

                  {myOrganization?.id && !!myOrganization?.download_hours_limit && (
                    <Tooltip placement="right" title={`Used ${hours}h ${minutes}m out of ${myOrganization.download_hours_limit}h`}>
                      <div className={styles.minimizedUsageCard}>
                        <img
                          className={styles.downloadHoursIcon}
                          style={{
                            marginBottom: '6px',
                            marginTop: '12px',
                            filter:
                              +downloadHoursLimitRatio.toFixed(1) > 50 && +downloadHoursLimitRatio.toFixed(1) < 95
                                ? 'invert(23%) sepia(100%) saturate(7490%) hue-rotate(-10deg) brightness(96%) contrast(112%)'
                                : +downloadHoursLimitRatio.toFixed(1) >= 95
                                  ? 'invert(30%) sepia(90%) saturate(6000%) hue-rotate(-10deg) brightness(80%) contrast(120%)'
                                  : 'none',
                          }}
                          src={DownloadHoursIcon}
                          alt="download hours"
                        />

                        <div
                          className={cx(
                            styles.storageRatio,
                            +downloadHoursLimitRatio.toFixed(1) > 50 && +downloadHoursLimitRatio.toFixed(1) < 95 ? styles.yellowText : '',
                            +downloadHoursLimitRatio.toFixed(1) >= 95 ? styles.redText : '',
                          )}
                        >
                          {downloadHoursLimitRatio === downloadHoursLimitRatio && downloadHoursLimitRatio.toFixed() + '%'}
                        </div>
                        <div
                          className={cx(
                            styles.storageUsedText,
                            +downloadHoursLimitRatio.toFixed(1) > 50 && +downloadHoursLimitRatio.toFixed(1) < 95 ? styles.yellowText : '',
                            +downloadHoursLimitRatio.toFixed(1) >= 95 ? styles.redText : '',
                          )}
                        >
                          {' '}
                          Download <br /> used{' '}
                        </div>
                      </div>
                    </Tooltip>
                  )}
                </div>
              )}
            </div>
          )}
        <div className={cx(styles.sidebarFooter, compactSidebar && styles.sidebarFooterCompact)}>
          <div className={styles.sidebarInfo}>
            {playerSidebar && (
              <Tooltip title={<div>{shortcutsInfo.map((info) => <div key={info} style={{ padding: '4px' }}>{info}</div>)}</div>} arrow placement="top">
                <button type="button" className={styles.infoButton} aria-label="Keyboard shortcuts">
                  <HelpOutlineOutlinedIcon />
                </button>
              </Tooltip>
            )}
            <span className={styles.footerVersion} title={`Amplify version: ${FE_VERSION}`}>{FE_VERSION}</span>
          </div>
          <button type="button" className={styles.minimizeBtn} onClick={switchSidebarView} aria-expanded={!compactSidebar}
            aria-label={compactSidebar ? 'Expand sidebar' : 'Minimize sidebar'} title={compactSidebar ? 'Expand sidebar' : 'Minimize sidebar'}>
            {!compactSidebar && <span>Minimize sidebar</span>}
            <img src={MinimizeIcon} alt="" />
          </button>
        </div>
      </div>

      {additionalAngleUploadFeed && (
        <AdditionalAnglesUploadModal
          open
          minimizable
          videofeed={additionalAngleUploadFeed}
          onClose={() => dispatch(updateAdditionalAngleUploadFeed(null))}
          onSuccess={() => {
            bypassCacheForNextVideofeedsListFetch();
            dispatch(refetchFeeds());
          }}
        />
      )}

      <ComposerDownloadRoot />
    </>
  );
};

export default Sidebar;
